import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import type { CallInCadenceRow } from '@metron/shared';
import { Button } from './ui/button.tsx';
import { DataTable } from './data-table/data-table.tsx';
import { DataTableColumnHeader } from './data-table/data-table-column-header.tsx';
import { downloadCsv, toCsv } from '../lib/csv.ts';
import type { CsvColumn } from '../lib/csv.ts';
import { useTableUrlState } from '../hooks/useTableUrlState.ts';

export interface CallInCadenceTableProps {
  rows: CallInCadenceRow[];
  customerSlug: string;
  windowEnd: string;
}

const CSV_COLUMNS: CsvColumn<CallInCadenceRow>[] = [
  { header: 'Device ID', value: (r) => r.deviceId },
  { header: 'Property', value: (r) => r.propertyName },
  { header: 'Meter Type', value: (r) => r.meterType },
  { header: 'Avg Gap Days', value: (r) => r.avgGapDays },
  { header: 'Call-in Count', value: (r) => r.callInCount },
  { header: 'First Call-in', value: (r) => r.firstCallInAt },
  { header: 'Last Call-in', value: (r) => r.lastCallInAt },
];

const dim = <span className='text-muted-foreground'>—</span>;

export function CallInCadenceTable({ rows, customerSlug, windowEnd }: CallInCadenceTableProps) {
  const tableState = useTableUrlState();

  const columns = useMemo<ColumnDef<CallInCadenceRow>[]>(() => [
    {
      accessorKey: 'deviceId',
      header: ({ column }) => <DataTableColumnHeader column={column} title='Device ID' />,
      cell: ({ row }) =>
        row.original.deviceId === null ? dim : (
          <Link
            to={`/meter-inspector/${row.original.sensorId}`}
            className='font-mono text-primary hover:underline'
          >
            {row.original.deviceId}
          </Link>
        ),
    },
    {
      accessorKey: 'propertyName',
      header: ({ column }) => <DataTableColumnHeader column={column} title='Property' />,
      cell: ({ row }) => row.original.propertyName ?? dim,
    },
    {
      accessorKey: 'meterType',
      header: ({ column }) => <DataTableColumnHeader column={column} title='Meter Type' />,
      cell: ({ row }) => row.original.meterType ?? dim,
    },
    {
      accessorKey: 'avgGapDays',
      header: ({ column }) => <DataTableColumnHeader column={column} title='Avg Days' />,
      cell: ({ row }) =>
        row.original.avgGapDays === null || row.original.avgGapDays === undefined
          ? dim
          : Number(row.original.avgGapDays).toFixed(1),
      // Push nulls to the bottom regardless of sort direction so silent meters
      // don't dominate the top of an ascending sort.
      sortUndefined: 'last',
    },
    {
      accessorKey: 'callInCount',
      header: ({ column }) => <DataTableColumnHeader column={column} title='Call-in Count' />,
      cell: ({ row }) => row.original.callInCount,
    },
    {
      accessorKey: 'firstCallInAt',
      header: ({ column }) => <DataTableColumnHeader column={column} title='First' />,
      cell: ({ row }) => row.original.firstCallInAt ?? dim,
    },
    {
      accessorKey: 'lastCallInAt',
      header: ({ column }) => <DataTableColumnHeader column={column} title='Last' />,
      cell: ({ row }) => row.original.lastCallInAt ?? dim,
    },
  ], []);

  const handleExportCsv = () => {
    const filename = `call-in-cadence-${customerSlug}-${windowEnd.slice(0, 10)}.csv`;
    downloadCsv(filename, toCsv(rows, CSV_COLUMNS));
  };

  return (
    <div className='space-y-2'>
      <div className='flex justify-between items-center'>
        <p className='text-sm text-muted-foreground'>
          {rows.length} {rows.length === 1 ? 'meter' : 'meters'}
        </p>
        <Button variant='outline' size='sm' onClick={handleExportCsv}>
          Export CSV
        </Button>
      </div>
      <DataTable
        columns={columns}
        data={rows}
        sorting={tableState.sorting}
        onSortingChange={tableState.onSortingChange}
        pagination={tableState.pagination}
        onPaginationChange={tableState.onPaginationChange}
      />
    </div>
  );
}
